import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    ChevronLeft,
    Loader2,
    UserCheck,
    Stethoscope,
    Send,
    Mail,
    Check,
    MessageSquare,
    AlertTriangle,
} from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import GuarantorsPanel from '@/components/GuarantorsPanel';
import EmployerReferencesPanel from '@/components/EmployerReferencesPanel';
import InvestigationDocumentsPanel from '@/components/InvestigationDocumentsPanel';
import MedicalLabSection from '@/components/MedicalLabSection';
import SocialMediaPanel from '@/components/SocialMediaPanel';
import CsChecklistPanel from '@/components/CsChecklistPanel';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import {
    useGetCsInvestigationQuery,
    useGetCsAssignableUsersQuery,
    useCsAssignAgentMutation,
    useCsAssignMedicalMutation,
    useCsNotifyEmployeeMutation,
    useCsUpdateEmployerMutation,
    useSendSmsMutation,
} from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { User } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'CS';
}

function fullName(first?: string, last?: string) {
    return `${first ?? ''} ${last ?? ''}`.trim();
}

function statusLabel(status: string) {
    return status
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

export default function CustomerSuccessDetail() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const currentUser = useAppSelector(selectCurrentUser);

    const { data: investigation, isLoading, error } = useGetCsInvestigationQuery(id || '', { skip: !id });

    if (isLoading) {
        return (
            <main className="w-full h-screen flex items-center justify-center bg-[#EDEFF3]">
                <Loader2 className="h-10 w-10 text-[#FF6B35] animate-spin" />
            </main>
        );
    }

    if (error || !investigation) {
        return (
            <main className="w-full h-screen flex items-center justify-center bg-[#EDEFF3]">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Failed to load investigation.</p>
                    <button
                        onClick={() => navigate('/customer-success/investigations')}
                        className="px-6 py-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800"
                    >
                        Back to investigations
                    </button>
                </div>
            </main>
        );
    }

    const employeeName =
        fullName(investigation.employeeFirstName, investigation.employeeLastName) || investigation.employeeEmail;
    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Customer Success'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'CS'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6">
                    {/* Header */}
                    <div className="flex items-start gap-4">
                        <button
                            onClick={() => navigate('/customer-success/investigations')}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200"
                        >
                            <ChevronLeft className="h-5 w-5 text-gray-600" />
                        </button>
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{employeeName}</h1>
                            <Badge
                                variant="outline"
                                className="bg-gray-50 text-gray-700 border-gray-200 px-3 py-1 rounded-lg font-medium"
                            >
                                {statusLabel(investigation.status)}
                            </Badge>
                            {investigation.medicalAnalysisIncluded && (
                                <Badge
                                    variant="outline"
                                    className="bg-rose-50 text-rose-700 border-rose-100 px-3 py-1 rounded-lg font-medium"
                                >
                                    Medical Analysis
                                </Badge>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left: summary */}
                        <div className="lg:col-span-1 flex flex-col gap-4">
                            <SummaryCard investigation={investigation} />
                            <SocialMediaPanel
                                handles={investigation.socialMediaHandles}
                                consentGiven={investigation.consentSocialMediaReview}
                            />
                        </div>

                        {/* Right: actions */}
                        <div className="lg:col-span-2 flex flex-col gap-6">
                            <CsChecklistPanel investigationId={investigation.id} canEdit />
                            <AssignAgentCard investigationId={investigation.id} currentAgent={investigation.agent} />
                            <AssignMedicalCard
                                investigationId={investigation.id}
                                currentMedical={investigation.medicalUser}
                            />
                            <MedicalLabSection
                                investigationId={investigation.id}
                                variant="customer-success"
                                locationHint={investigation.employmentTown}
                                medicalLabId={investigation.medicalLabId}
                                medicalLabAssignedAt={investigation.medicalLabAssignedAt}
                            />
                            <NotifyEmployeeCard investigationId={investigation.id} />
                            <SendSmsCard defaultPhone={investigation.employeePhone} />
                            <UpdateEmployerCard investigationId={investigation.id} />

                            <div className="rounded-2xl border border-gray-100 p-5">
                                <GuarantorsPanel
                                    investigationId={investigation.id}
                                    canManage={false}
                                    canReassign
                                    reassignVariant="customer-success"
                                    guarantorTownWarning={investigation.guarantorTownWarning}
                                    employmentTown={investigation.employmentTown}
                                    showCallWindow
                                />
                            </div>

                            {/* Previous-employer references — CS views; retrigger is Super Admin only. */}
                            <div className="rounded-2xl border border-gray-100 p-5">
                                <EmployerReferencesPanel investigationId={investigation.id} canRetrigger={false} />
                            </div>

                            {/* Documents — CS can upload / overwrite / delete any document. */}
                            <div className="rounded-2xl border border-gray-100 p-5">
                                <InvestigationDocumentsPanel investigationId={investigation.id} canManage />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

function SummaryCard({ investigation }: { investigation: import('@/types').Investigation }) {
    const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
        <div className="flex flex-col gap-0.5">
            <span className="text-[11px] uppercase tracking-wider text-gray-400">{label}</span>
            <span className="text-sm font-medium text-gray-900">{value || '—'}</span>
        </div>
    );

    return (
        <div className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-4">
            <h3 className="text-sm font-bold text-gray-900">Investigation</h3>
            <Row label="Tier" value={<span className="capitalize">{investigation.checkType}</span>} />
            <Row
                label="Category"
                value={
                    investigation.investigationCategory ? (
                        <span className="capitalize">{investigation.investigationCategory.replace('_', ' ')}</span>
                    ) : (
                        '—'
                    )
                }
            />
            <Row label="Employee email" value={investigation.employeeEmail} />
            <Row label="Employee phone" value={investigation.employeePhone} />
            <Row
                label="Employer"
                value={
                    investigation.employer
                        ? investigation.employer.companyName ||
                          fullName(investigation.employer.firstName, investigation.employer.lastName)
                        : '—'
                }
            />
            <Row label="Employment town" value={investigation.employmentTown} />
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Assign agent
// ──────────────────────────────────────────────────────────────────────────────

function AssignAgentCard({
    investigationId,
    currentAgent,
}: {
    investigationId: string;
    currentAgent?: User | null | undefined;
}) {
    const { data: agents = [] } = useGetCsAssignableUsersQuery({ role: 'agent' });
    const [assignAgent, { isLoading }] = useCsAssignAgentMutation();
    const [selected, setSelected] = useState('');
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const handleAssign = async () => {
        if (!selected) return;
        setError('');
        setDone(false);
        try {
            await assignAgent({ id: investigationId, agentId: selected }).unwrap();
            setDone(true);
            setSelected('');
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to assign agent.'));
        }
    };

    return (
        <ActionCard
            icon={<UserCheck className="h-4 w-4 text-[#FF6B35]" />}
            title={currentAgent ? 'Reassign field agent' : 'Assign field agent'}
            subtitle={
                currentAgent
                    ? `Currently ${fullName(currentAgent.firstName, currentAgent.lastName)}.`
                    : 'No agent assigned yet.'
            }
        >
            <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#FF6B35]"
            >
                <option value="">Select an agent…</option>
                {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                        {fullName(a.firstName, a.lastName)} {a.city ? `— ${a.city}` : ''}
                    </option>
                ))}
            </select>
            <ActionFooter
                onSubmit={handleAssign}
                isLoading={isLoading}
                disabled={!selected}
                done={done}
                error={error}
                label={currentAgent ? 'Reassign agent' : 'Assign agent'}
            />
        </ActionCard>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Assign medical user
// ──────────────────────────────────────────────────────────────────────────────

function AssignMedicalCard({
    investigationId,
    currentMedical,
}: {
    investigationId: string;
    currentMedical?: User | null | undefined;
}) {
    const { data: medicalUsers = [] } = useGetCsAssignableUsersQuery({ role: 'medical' });
    const [assignMedical, { isLoading }] = useCsAssignMedicalMutation();
    const [selected, setSelected] = useState('');
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const handleAssign = async () => {
        if (!selected) return;
        setError('');
        setDone(false);
        try {
            await assignMedical({ id: investigationId, medicalUserId: selected }).unwrap();
            setDone(true);
            setSelected('');
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to assign medical user.'));
        }
    };

    return (
        <ActionCard
            icon={<Stethoscope className="h-4 w-4 text-[#FF6B35]" />}
            title={currentMedical ? 'Reassign medical user' : 'Assign medical user'}
            subtitle={
                currentMedical
                    ? `Currently ${fullName(currentMedical.firstName, currentMedical.lastName)}.`
                    : 'No medical user assigned yet.'
            }
        >
            <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#FF6B35]"
            >
                <option value="">Select a medical user…</option>
                {medicalUsers.map((m) => (
                    <option key={m.id} value={m.id}>
                        {fullName(m.firstName, m.lastName)} — {m.email}
                    </option>
                ))}
            </select>
            <ActionFooter
                onSubmit={handleAssign}
                isLoading={isLoading}
                disabled={!selected}
                done={done}
                error={error}
                label={currentMedical ? 'Reassign medical user' : 'Assign medical user'}
            />
        </ActionCard>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Notify employee
// ──────────────────────────────────────────────────────────────────────────────

function NotifyEmployeeCard({ investigationId }: { investigationId: string }) {
    const [notify, { isLoading }] = useCsNotifyEmployeeMutation();
    return (
        <MessageCard
            icon={<Send className="h-4 w-4 text-[#FF6B35]" />}
            title="Notify the employee"
            subtitle="Sends WhatsApp + email — test go-ahead, police-capture info, next steps."
            buttonLabel="Send to employee"
            isLoading={isLoading}
            defaultSubject="Next steps for your check"
            placeholder="e.g. Please proceed with your medical test at XYZ Clinic, and do your police capture at the Ikeja station on Monday."
            onSubmit={(data) => notify({ id: investigationId, data }).unwrap()}
        />
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Send SMS (to the employee phone)
// ──────────────────────────────────────────────────────────────────────────────

function SendSmsCard({ defaultPhone }: { defaultPhone?: string }) {
    const [sendSms, { isLoading }] = useSendSmsMutation();
    const [phone, setPhone] = useState(defaultPhone ?? '');
    const [message, setMessage] = useState('');
    const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);

    const handleSend = async () => {
        if (!phone.trim() || !message.trim()) return;
        setFeedback(null);
        try {
            const res = await sendSms({ phoneNumber: phone.trim(), message: message.trim() }).unwrap();
            if (res.success) {
                setFeedback({ ok: true, text: `SMS sent to ${res.phoneNumber}.` });
                setMessage('');
            } else {
                setFeedback({ ok: false, text: "Couldn't send the SMS. Please try another channel." });
            }
        } catch (err) {
            setFeedback({ ok: false, text: parseErrorMessage(err, 'Failed to send the SMS.') });
        }
    };

    return (
        <ActionCard
            icon={<MessageSquare className="h-4 w-4 text-[#FF6B35]" />}
            title="Send an SMS"
            subtitle="Text the employee directly (separate from the WhatsApp + email notification)."
        >
            <input
                type="tel"
                value={phone}
                onChange={(e) => {
                    setPhone(e.target.value);
                    setFeedback(null);
                }}
                placeholder="Phone number"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6B35]"
            />
            <textarea
                value={message}
                onChange={(e) => {
                    setMessage(e.target.value);
                    setFeedback(null);
                }}
                rows={3}
                placeholder="e.g. Hi, your background check is underway. Reply to this number if you have questions."
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[#FF6B35]"
            />
            {feedback && (
                <p className={`flex items-center gap-1.5 text-xs ${feedback.ok ? 'text-green-600' : 'text-amber-700'}`}>
                    {feedback.ok ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    {feedback.text}
                </p>
            )}
            <button
                type="button"
                onClick={handleSend}
                disabled={!phone.trim() || !message.trim() || isLoading}
                className="self-start bg-[#1b1b1b] hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
            >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Send SMS
            </button>
        </ActionCard>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Update employer
// ──────────────────────────────────────────────────────────────────────────────

function UpdateEmployerCard({ investigationId }: { investigationId: string }) {
    const [update, { isLoading }] = useCsUpdateEmployerMutation();
    return (
        <MessageCard
            icon={<Mail className="h-4 w-4 text-[#FF6B35]" />}
            title="Update the employer"
            subtitle="Sends an email update to the employer."
            buttonLabel="Send to employer"
            isLoading={isLoading}
            defaultSubject="Update"
            placeholder="e.g. Fieldwork is underway; report expected within your 14–21 day window."
            onSubmit={(data) => update({ id: investigationId, data }).unwrap()}
        />
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Shared building blocks
// ──────────────────────────────────────────────────────────────────────────────

function ActionCard({
    icon,
    title,
    subtitle,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-3">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-50">
                    {icon}
                </div>
                <div>
                    <h3 className="text-sm font-bold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
                </div>
            </div>
            {children}
        </div>
    );
}

function ActionFooter({
    onSubmit,
    isLoading,
    disabled,
    done,
    error,
    label,
}: {
    onSubmit: () => void;
    isLoading: boolean;
    disabled: boolean;
    done: boolean;
    error: string;
    label: string;
}) {
    return (
        <>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <button
                type="button"
                onClick={onSubmit}
                disabled={disabled || isLoading}
                className="self-start bg-[#1b1b1b] hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
            >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : done ? <Check className="w-4 h-4" /> : null}
                {done ? 'Done' : label}
            </button>
        </>
    );
}

function MessageCard({
    icon,
    title,
    subtitle,
    buttonLabel,
    isLoading,
    defaultSubject,
    placeholder,
    onSubmit,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    buttonLabel: string;
    isLoading: boolean;
    defaultSubject: string;
    placeholder: string;
    onSubmit: (data: { message: string; subject?: string }) => Promise<unknown>;
}) {
    const [subject, setSubject] = useState(defaultSubject);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [sent, setSent] = useState(false);

    const handleSend = async () => {
        if (!message.trim()) return;
        setError('');
        setSent(false);
        try {
            await onSubmit({ message: message.trim(), ...(subject.trim() ? { subject: subject.trim() } : {}) });
            setSent(true);
            setMessage('');
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to send message.'));
        }
    };

    return (
        <ActionCard icon={icon} title={title} subtitle={subtitle}>
            <input
                type="text"
                value={subject}
                onChange={(e) => {
                    setSubject(e.target.value);
                    setSent(false);
                }}
                placeholder="Subject"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6B35]"
            />
            <textarea
                value={message}
                onChange={(e) => {
                    setMessage(e.target.value);
                    setSent(false);
                }}
                rows={3}
                placeholder={placeholder}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[#FF6B35]"
            />
            <ActionFooter
                onSubmit={handleSend}
                isLoading={isLoading}
                disabled={!message.trim()}
                done={sent}
                error={error}
                label={buttonLabel}
            />
        </ActionCard>
    );
}
